import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step2 from "@/components/menu-management/special-menu/buffet/add-buffet/step2";
import Step1 from "@/components/menu-management/special-menu//buffet/add-buffet/step1";
import Step3 from "@/components/menu-management/special-menu/buffet/add-buffet/step3";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewBuffet,
  clearBuffetMessages,
  updateExistingBuffet,
} from "@/redux/slice/menu-manage-slice/special-menu/buffets-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";

export const menu_id = 17;

function CreateBuffet() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  const { createLoading, updateLoading } = useSelector(
    (state) => state.menuManage.buffet
  );
  const { getLoading } = useSelector((state) => state.menuManage.menu);

  const form = useForm({
    resolver: zodResolver(createBuffet),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: "",
      name: "",
      description: "",
      vat: 0,
      status: "active",
      adult_price: null,
      kids_price: null,
      time: null,
      buffet_items: [],
      file_manager_id: undefined,
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user.restaurant_id));
  }, []);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/special-menu/buffet");
      return;
    }
    form.reset({
      type: "update",
      item_no: item.item_no || "",
      name: item.name || "",
      description: item.description || "",
      adult_price: item.adult_price || null,
      kids_price: item.kids_price || null,
      time: item.time || null,
      vat: item.vat ?? 0,
      status: item.status || "active",
      file_manager_id: item.file_manager_id || undefined,
    });

    if (item.file_manager_id && item.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    dispatch(setItemVariants(item?.buffet_items));
  }, [type, item?.id, form]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Buffet",
      user,
      createAction: createNewBuffet,
      updateAction: updateExistingBuffet,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/special-menu/buffet",
    });

  if (getLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${type == "update" ? "Update Buffet" : "Add New Buffet"}`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "BackoffBack Officeice", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        {
          name: "Special Menu",
          path: "/backoffice/menu/special-menu/dish-the-day",
        },
        {
          name: "Buffet",
          path: "/backoffice/menu/special-menu/buffet",
        },
        {
          name: `${type == "update" ? "Update Buffet" : "Add New Buffet"}`,
          path: `/backoffice/menu/special-menu/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 menu_id={menu_id} form={form} />}
            {step === 1 && (
              <Step2
                item_id={entityId || item?.id}
                menu_id={menu_id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/special-menu/buffet"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateBuffet;

export const createBuffet = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  buffet_items: z.array(z.object({}).catchall(z.any())).optional(),
  adult_price: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),
  kids_price: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),
  time: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
